import { create } from "zustand";
import { Account } from "~/types/partners";
import { BPFPaymentInformation } from "~/types/bpf";
import { Contract } from "~/types/contract";

type BPFGeneralInformation = {
    bpfName: string;
    paymentReference: string;
    evidence: File | undefined;
    contractPartner: Account | undefined;
};

export type ClaimBPFMap = {
    contractId: string;
    reference: string;
    claims: string[];
};

type BPFOutcome = {
    contractId: string;
    reference: string;
    outcomes: Array<string>;
};

const defaultGenerateBpf = {
    outcomes: [],
    claims: [],
    bpfName: "",
    paymentReference: "",
    contractId: "",
};

const defaultPerformanceSteps = [
    {
        id: "general-information",
        title: "General information",
        description: "",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "select-contract",
        title: "Select contract",
        description: "",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "select-outcomes",
        title: "Select outcomes",
        description: "",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "payment-information",
        title: "Payment information",
        description: "",
        isPrepared: false,
        isCompleted: false,
    },
];

const defaultVolumeSteps = [
    {
        id: "general-information",
        title: "General information",
        description: "",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "select-contract",
        title: "Select contract",
        description: "",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "select-claims",
        title: "Select claims",
        description: "",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "payment-information",
        title: "Payment information",
        description: "",
        isPrepared: false,
        isCompleted: false,
    },
];

const defaultApheresisSteps = [
    {
        id: "general-information",
        title: "General information",
        description: "",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "select-contract",
        title: "Select contract",
        description: "",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "select-outcomes",
        title: "Select outcomes",
        description: "",
        isPrepared: false,
        isCompleted: false,
    },
    {
        id: "payment-information",
        title: "Payment information",
        description: "",
        isPrepared: false,
        isCompleted: false,
    },
];

interface CreateBPFState {
    generatePerformanceBasedBpfSteps: any;
    setGeneratePerformanceBasedBpfSteps: (steps: any) => void;

    generateVolumeBasedBpfSteps: any;
    setGenerateVolumeBasedBpfSteps: (steps: any) => void;

    generateApheresisBasedBpfSteps: any;
    setGenerateApheresisBasedBpfSteps: (steps: any) => void;

    selectClaims: ClaimBPFMap[];
    setSelectClaims: (claims: any) => void;

    selectContracts: Contract[];
    setSelectContracts: (contracts: any) => void;

    selectOutcomes: BPFOutcome[];
    setSelectOutcomes: (outcomes: any) => void;

    generalInformation: BPFGeneralInformation;
    setGeneralInformation: (info: any) => void;

    paymentInformation: BPFPaymentInformation;
    setPaymentInformation: (info: any) => void;

    resetBPFData: () => void;

    resetSelectClaims: () => void;
    resetSelectContracts: () => void;
    resetSelectOutcomes: () => void;
}

// Default values for reset
const DEFAULT_GENERAL_INFORMATION: BPFGeneralInformation = {
    bpfName: "",
    paymentReference: "",
    evidence: undefined,
    contractPartner: undefined,
};

const DEFAULT_PAYMENT_INFORMATION: BPFPaymentInformation =
    {} as BPFPaymentInformation;

export const useCreateBPFStore = create<CreateBPFState>(set => ({
    generatePerformanceBasedBpfSteps: defaultPerformanceSteps,
    setGeneratePerformanceBasedBpfSteps: generatePerformanceBasedBpfSteps =>
        set({ generatePerformanceBasedBpfSteps }),

    generateVolumeBasedBpfSteps: defaultVolumeSteps,
    setGenerateVolumeBasedBpfSteps: generateVolumeBasedBpfSteps =>
        set({ generateVolumeBasedBpfSteps }),

    generateApheresisBasedBpfSteps: defaultApheresisSteps,
    setGenerateApheresisBasedBpfSteps: generateApheresisBasedBpfSteps =>
        set({ generateApheresisBasedBpfSteps }),

    selectClaims: [] as ClaimBPFMap[],
    setSelectClaims: selectClaims => set({ selectClaims }),

    selectContracts: [] as Contract[],
    setSelectContracts: selectContracts => set({ selectContracts }),

    selectOutcomes: [] as BPFOutcome[],
    setSelectOutcomes: selectOutcomes => set({ selectOutcomes }),

    generalInformation: DEFAULT_GENERAL_INFORMATION,
    setGeneralInformation: generalInformation => set({ generalInformation }),

    paymentInformation: DEFAULT_PAYMENT_INFORMATION,
    setPaymentInformation: paymentInformation => set({ paymentInformation }),
    resetBPFPaymentInformation: () =>
        set({ paymentInformation: DEFAULT_PAYMENT_INFORMATION }),
    resetSelectClaims: () => set({ selectClaims: [] as ClaimBPFMap[] }),
    resetSelectContracts: () => set({ selectContracts: [] as Contract[] }),
    resetSelectOutcomes: () => set({ selectOutcomes: [] as BPFOutcome[] }),

    resetBPFData: () =>
        set({
            selectClaims: [] as ClaimBPFMap[],
            selectContracts: [] as Contract[],
            selectOutcomes: [] as BPFOutcome[],
            // Create NEW object references to trigger Formik's enableReinitialize
            generalInformation: { ...DEFAULT_GENERAL_INFORMATION },
            paymentInformation: { ...DEFAULT_PAYMENT_INFORMATION },
            generatePerformanceBasedBpfSteps: defaultPerformanceSteps,
            generateVolumeBasedBpfSteps: defaultVolumeSteps,
            generateApheresisBasedBpfSteps: defaultApheresisSteps,
        }),
}));
